import { FC, ReactNode, useCallback, useEffect, useState } from 'react';

import { FlowsControllerContext } from './FlowsControllerContext';

import {
	TBankStatementsData,
	TDebtScheduleData,
	THandoffMode,
	TNavigationSource,
	TUploadAttachmentsData,
	sessionStorageCache,
	useNavigationSource,
} from '@/shared';

type TFlowsControllerProviderProps = {
	children: ReactNode;
};

export const FlowsControllerProvider: FC<TFlowsControllerProviderProps> = ({ children }) => {
	const [bankStatementData, setBankStatementDataState] = useState<TBankStatementsData | null>(() =>
		sessionStorageCache.get<TBankStatementsData>('bankStatementData'),
	);
	const [debtScheduleData, setDebtScheduleDataState] = useState<TDebtScheduleData | null>(() =>
		sessionStorageCache.get('debtScheduleData'),
	);
	const [isContinuationOfTheMainFlow, setIsContinuationOfTheMainFlowState] = useState<boolean>(
		() => sessionStorageCache.get<boolean>('isContinuationOfTheMainFlow') ?? false,
	);
	const [uploadAttachmentsData, setUploadAttachmentsDataState] =
		useState<TUploadAttachmentsData | null>(() => sessionStorageCache.get('uploadAttachmentsData'));
	const [handoffMode, setHandoffModeState] = useState<THandoffMode | null>(() =>
		sessionStorageCache.get<THandoffMode>('handoffMode'),
	);

	const setBankStatementData = useCallback((data: TBankStatementsData | null): void => {
		setBankStatementDataState(data);
		sessionStorageCache.set('bankStatementData', data);
	}, []);

	const setDebtScheduleData = useCallback((data: TDebtScheduleData | null): void => {
		setDebtScheduleDataState(data);
		sessionStorageCache.set('debtScheduleData', data);
	}, []);

	const setIsContinuationOfTheMainFlow = useCallback((isContinuation: boolean): void => {
		setIsContinuationOfTheMainFlowState(isContinuation);
		sessionStorageCache.set('isContinuationOfTheMainFlow', isContinuation);
	}, []);

	const setUploadAttachmentsData = useCallback((data: TUploadAttachmentsData | null): void => {
		setUploadAttachmentsDataState(data);
		sessionStorageCache.set('uploadAttachmentsData', data);
	}, []);

	const setHandoffMode = useCallback((mode: THandoffMode | null): void => {
		setHandoffModeState(mode);
		sessionStorageCache.set('handoffMode', mode);
	}, []);

	const source = useNavigationSource();

	useEffect(() => {
		const availableSources = [
			'initial-load',
			'internal',
			'back-forward',
			'reload',
		] as TNavigationSource[];

		if (availableSources.includes(source)) return;

		setIsContinuationOfTheMainFlow(false);
	}, [source]);

	return (
		<FlowsControllerContext.Provider
			value={{
				bankStatementData,
				setBankStatementData,
				debtScheduleData,
				setDebtScheduleData,
				isContinuationOfTheMainFlow,
				setIsContinuationOfTheMainFlow,
				uploadAttachmentsData,
				setUploadAttachmentsData,
				handoffMode,
				setHandoffMode,
			}}
		>
			{children}
		</FlowsControllerContext.Provider>
	);
};
